"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { apiClient } from "@/lib/api-client";
import { useAuth } from "@workspace/ui/hooks/useAuth";
import { Card } from "@workspace/ui/components/card";
import { Badge } from "@workspace/ui/components/badge";
import { Button } from "@workspace/ui/components/button";
import {
  AlertCircle,
  CheckSquare,
  Clock,
  Eye,
  AtSign,
  History,
  HelpCircle,
} from "lucide-react";

interface ActionItem {
  id: string;
  docId: string;
  docTitle: string;
  item: string;
  due: number | null;
  status: string;
  owner: string | null;
  sourceField: string;
}

interface ReviewItem {
  docId: string;
  title: string;
  type: string;
  status: string | null;
  role: "reviewer" | "approver";
}

interface OwnedItem {
  docId: string;
  title: string;
  type: string;
  status: string | null;
  nextReview: number | null;
}

interface BlockedItem {
  atomId: string;
  docId: string;
  docTitle: string;
  title: string;
  waitingOn: string[];
  asserted: boolean;
}

interface MentionItem {
  docId: string;
  title: string;
  extractedAt: number;
}

interface DueSoonItem {
  docId: string;
  title: string;
  type: string;
  kind: "review" | "task";
  label: string;
  dueAt: number;
  overdue: boolean;
}

interface RecentChange {
  id: number;
  docId: string;
  docTitle: string | null;
  field: string;
  kind: string;
  prev: unknown;
  next: unknown;
  actor: string | null;
  source: string;
  createdAt: number;
}

interface MeResponse {
  viewer: { handles: string[]; email: string; name: string };
  actionItems: ActionItem[];
  reviewsPending: ReviewItem[];
  owned: OwnedItem[];
  blocked: BlockedItem[];
  mentions: MentionItem[];
  dueSoon: DueSoonItem[];
  recent: RecentChange[];
}

export default function MePage() {
  const { authenticated, loading } = useAuth({ requireAuth: true });
  const [data, setData] = useState<MeResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!authenticated) return;
    const es = new EventSource("/api/me/stream", { withCredentials: true });
    es.addEventListener("snapshot", (ev) => {
      try {
        setData(JSON.parse((ev as MessageEvent).data) as MeResponse);
        setError(null);
      } catch (e) {
        setError(String(e));
      }
    });
    es.addEventListener("error", () => {
      // EventSource auto-reconnects; only surface error if we have no data yet
      setError((prev) => prev ?? "Connection lost — retrying…");
    });
    return () => {
      es.close();
    };
  }, [authenticated]);

  if (loading || !authenticated) {
    return (
      <div className="container max-w-6xl py-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-48 bg-muted rounded" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container max-w-6xl py-8">
        <p className="text-destructive">Failed to load: {error}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="container max-w-6xl py-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-48 bg-muted rounded" />
          <div className="grid grid-cols-2 gap-4">
            <div className="h-40 bg-muted rounded-lg" />
            <div className="h-40 bg-muted rounded-lg" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container max-w-6xl py-8 space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">My workspace</h1>
        <p className="text-sm text-muted-foreground">
          {data.viewer.name} — handles: {data.viewer.handles.join(", ")}
        </p>
      </header>

      <DueSoonSection items={data.dueSoon} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ActionItemsSection items={data.actionItems} />
        <ReviewsSection items={data.reviewsPending} />
      </div>

      <BlockedSection items={data.blocked} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <OwnedSection items={data.owned} />
        <MentionsSection items={data.mentions} />
      </div>

      <RecentChangesSection items={data.recent} />
    </div>
  );
}

function SectionHeader({
  icon: Icon,
  title,
  count,
}: {
  icon: typeof Clock;
  title: string;
  count: number;
}) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <Icon className="h-4 w-4 text-muted-foreground" />
      <h2 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
        {title}
      </h2>
      <Badge variant="secondary" className="ml-auto">
        {count}
      </Badge>
    </div>
  );
}

/**
 * Unresolved choices in your docs that point at another doc (tdd-0100).
 *
 * The asserted/mentioned split is load-bearing, not decoration. A prose reference
 * proves the two are related, NOT that one gates the other — so an unpromoted row
 * says "mentions", and only a human-confirmed edge is allowed to read as a
 * dependency. Rendering both the same way would manufacture confidence the graph
 * has not earned.
 */
function BlockedSection({ items }: { items: BlockedItem[] }) {
  // Which row is mid-request. The SSE snapshot re-renders the section on success,
  // so there is no local optimistic copy to keep in sync with the server.
  const [busy, setBusy] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  if (items.length === 0) return null;

  async function send(
    fromId: string,
    toId: string,
    body: { action: "promote"; kind: string } | { action: "demote" }
  ) {
    const key = `${fromId}->${toId}`;
    setBusy(key);
    setFailed(null);
    try {
      await apiClient.post("/api/atoms/edges", { fromId, toId, ...body });
    } catch (e) {
      setFailed(`${key}: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(null);
    }
  }

  return (
    <Card className="p-4">
      <SectionHeader icon={HelpCircle} title="Blocked on a decision" count={items.length} />
      <ul className="space-y-3">
        {items.map((it) => (
          <li key={it.atomId} className="text-sm">
            <div className="flex items-baseline gap-2">
              <Link href={`/${it.docId}`} className="font-medium hover:underline shrink-0">
                {it.docId}
              </Link>
              <span className="truncate">{it.title}</span>
            </div>
            {it.waitingOn.map((ref) => {
              const key = `${it.atomId}->${ref}`;
              return (
                <div key={ref} className="flex items-center gap-2 mt-1 text-xs">
                  <Badge
                    variant={it.asserted ? "default" : "outline"}
                    className="font-normal"
                  >
                    {it.asserted ? "waiting on" : "mentions"}
                  </Badge>
                  <Link href={`/${ref}`} className="hover:underline">
                    {ref}
                  </Link>
                  {it.asserted ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-xs"
                      disabled={busy === key}
                      onClick={() => void send(it.atomId, ref, { action: "demote" })}
                    >
                      Unconfirm
                    </Button>
                  ) : (
                    <>
                      {/* The question the inference cannot answer: which way does it
                          run, and is it a dependency at all? "Just a reference" is a
                          real verdict and stops the row being re-offered forever. */}
                      <span className="text-muted-foreground">does it block?</span>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-6 px-2 text-xs"
                        disabled={busy === key}
                        onClick={() =>
                          void send(it.atomId, ref, { action: "promote", kind: "blocks" })
                        }
                      >
                        Yes
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs"
                        disabled={busy === key}
                        onClick={() =>
                          void send(it.atomId, ref, { action: "promote", kind: "cites" })
                        }
                      >
                        Just a reference
                      </Button>
                    </>
                  )}
                </div>
              );
            })}
          </li>
        ))}
      </ul>
      {failed && <p className="mt-2 text-xs text-destructive">{failed}</p>}
      <p className="mt-3 text-xs text-muted-foreground">
        {items.filter((i) => i.asserted).length} confirmed ·{" "}
        {items.filter((i) => !i.asserted).length} inferred from prose, direction
        unconfirmed
      </p>
    </Card>
  );
}

function DueSoonSection({ items }: { items: DueSoonItem[] }) {
  if (items.length === 0) return null;
  return (
    <Card className="p-4">
      <SectionHeader icon={Clock} title="Due soon" count={items.length} />
      <ul className="space-y-1">
        {items.map((it) => (
          <li
            key={`${it.kind}:${it.docId}:${it.dueAt}:${it.label}`}
            className="flex items-center gap-3 text-sm"
          >
            {it.overdue ? (
              <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
            ) : (
              <Clock className="h-4 w-4 text-muted-foreground shrink-0" />
            )}
            <span className="text-xs text-muted-foreground w-24 shrink-0">
              {formatDue(it.dueAt, it.overdue)}
            </span>
            <Badge variant="outline" className="text-xs">
              {it.kind === "review" ? "Review" : "Task"}
            </Badge>
            <Link
              href={`/${it.docId}`}
              className="hover:underline truncate flex-1"
            >
              {it.label} <span className="text-muted-foreground">— {it.title}</span>
            </Link>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function ActionItemsSection({ items }: { items: ActionItem[] }) {
  return (
    <Card className="p-4">
      <SectionHeader
        icon={CheckSquare}
        title="Action items"
        count={items.length}
      />
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nothing assigned to you.</p>
      ) : (
        <ul className="space-y-1">
          {items.map((it) => (
            <li key={it.id} className="flex items-center gap-2 text-sm">
              <Badge
                variant={it.status === "in-progress" ? "default" : "outline"}
                className="text-xs"
              >
                {it.status}
              </Badge>
              <Link
                href={`/${it.docId}`}
                className="truncate flex-1 hover:underline"
              >
                {it.item}
              </Link>
              {it.due && (
                <span className="text-xs text-muted-foreground shrink-0">
                  {formatDate(it.due)}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function ReviewsSection({ items }: { items: ReviewItem[] }) {
  return (
    <Card className="p-4">
      <SectionHeader icon={Eye} title="Reviews pending" count={items.length} />
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground">No reviews waiting on you.</p>
      ) : (
        <ul className="space-y-1">
          {items.map((it) => (
            <li key={it.docId} className="flex items-center gap-2 text-sm">
              <Badge variant="outline" className="text-xs">
                {it.type}
              </Badge>
              <Badge
                variant={it.role === "approver" ? "default" : "secondary"}
                className="text-xs"
              >
                {it.role}
              </Badge>
              <Link
                href={`/${it.docId}`}
                className="truncate flex-1 hover:underline"
              >
                {it.title}
              </Link>
              {it.status && (
                <span className="text-xs text-muted-foreground shrink-0">
                  {it.status}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function OwnedSection({ items }: { items: OwnedItem[] }) {
  return (
    <Card className="p-4">
      <SectionHeader
        icon={CheckSquare}
        title="Owned by you"
        count={items.length}
      />
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground">No docs owned.</p>
      ) : (
        <ul className="space-y-1">
          {items.slice(0, 20).map((it) => (
            <li key={it.docId} className="flex items-center gap-2 text-sm">
              <Badge variant="outline" className="text-xs">
                {it.type}
              </Badge>
              <Link
                href={`/${it.docId}`}
                className="truncate flex-1 hover:underline"
              >
                {it.title}
              </Link>
              {it.nextReview && (
                <span className="text-xs text-muted-foreground shrink-0">
                  next: {formatDate(it.nextReview)}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function MentionsSection({ items }: { items: MentionItem[] }) {
  return (
    <Card className="p-4">
      <SectionHeader icon={AtSign} title="Mentions" count={items.length} />
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground">No recent mentions.</p>
      ) : (
        <ul className="space-y-1">
          {items.slice(0, 15).map((it) => (
            <li key={it.docId} className="flex items-center gap-2 text-sm">
              <Link
                href={`/${it.docId}`}
                className="truncate flex-1 hover:underline"
              >
                {it.title}
              </Link>
              <span className="text-xs text-muted-foreground shrink-0">
                {formatDate(it.extractedAt)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function RecentChangesSection({ items }: { items: RecentChange[] }) {
  if (items.length === 0) return null;
  return (
    <Card className="p-4">
      <SectionHeader
        icon={History}
        title="Recent activity on your docs"
        count={items.length}
      />
      <ul className="space-y-1">
        {items.slice(0, 25).map((c) => (
          <li key={c.id} className="text-sm flex items-baseline gap-2">
            <span className="text-xs text-muted-foreground w-16 shrink-0">
              {formatDate(c.createdAt)}
            </span>
            <Link
              href={`/${c.docId}`}
              className="font-medium hover:underline shrink-0"
            >
              {c.docTitle ?? c.docId}
            </Link>
            <span className="text-muted-foreground truncate">
              {renderInlineChange(c)}
            </span>
            {c.actor && (
              <span className="text-xs text-muted-foreground shrink-0">
                by {c.actor}
              </span>
            )}
          </li>
        ))}
      </ul>
      <div className="mt-3">
        <Link href="/changes" className="text-sm text-primary hover:underline">
          See all changes →
        </Link>
      </div>
    </Card>
  );
}

function renderInlineChange(c: RecentChange): string {
  const fmt = (v: unknown): string => {
    if (v === null || v === undefined) return "∅";
    if (typeof v === "string") return v;
    if (typeof v === "number" || typeof v === "boolean") return String(v);
    if (Array.isArray(v)) return `[${v.length}]`;
    return JSON.stringify(v).slice(0, 60);
  };
  if (Array.isArray(c.prev) && Array.isArray(c.next)) {
    const ps = new Set((c.prev as unknown[]).map((x) => JSON.stringify(x)));
    const ns = new Set((c.next as unknown[]).map((x) => JSON.stringify(x)));
    const added = (c.next as unknown[]).filter(
      (x) => !ps.has(JSON.stringify(x))
    );
    const removed = (c.prev as unknown[]).filter(
      (x) => !ns.has(JSON.stringify(x))
    );
    const parts: string[] = [];
    if (added.length) parts.push(`+${added.map(fmt).join(", +")}`);
    if (removed.length) parts.push(`-${removed.map(fmt).join(", -")}`);
    return `${c.field}: ${parts.join(" ") || "(no change)"}`;
  }
  if (c.kind === "set") return `${c.field}: ∅ → ${fmt(c.next)}`;
  if (c.kind === "unset") return `${c.field}: ${fmt(c.prev)} → ∅`;
  if (c.kind === "added") return `${c.field}: + ${fmt(c.next)}`;
  if (c.kind === "removed") return `${c.field}: − ${fmt(c.prev)}`;
  return `${c.field}: ${fmt(c.prev)} → ${fmt(c.next)}`;
}

function formatDate(ts: number): string {
  const d = new Date(ts);
  const now = new Date();
  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: sameYear ? undefined : "numeric",
  });
}

function formatDue(ts: number, overdue: boolean): string {
  const d = new Date(ts);
  const days = Math.round((ts - Date.now()) / (24 * 60 * 60 * 1000));
  if (overdue) return `${Math.abs(days)}d overdue`;
  if (days === 0) return "today";
  if (days === 1) return "tomorrow";
  if (days <= 14) return `in ${days}d`;
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}
